import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import type { TriggerDef } from './trigger.service';

@Injectable({ providedIn: 'root' })
export class WhatsAppTriggerService {
  /** Default phone number for WhatsApp */
  readonly phone = '919810223204';

  /** Emits whenever the user actually opens WhatsApp (interceptor or direct) */
  readonly clicked$ = new Subject<void>();

  /** Currently active trigger message — used to override default WhatsApp text */
  private activeMessage: string | null = null;
  private activeTriggerId: string | null = null;
  private activeUntil = 0;
  /** Trigger message stays "fresh" for 5 min after firing */
  private readonly STALE_MS = 5 * 60 * 1000;

  private interceptorInstalled = false;
  /** TEMP: set true to block WhatsApp from opening while keeping triggers/tracking active */
  disableOpen = false;

  // Generated once per service lifetime so Date.now() never ticks between CD cycles
  private readonly sessionTrackingId =String(Date.now());

  constructor(private zone: NgZone) {}

  /** Set the active trigger message — next WhatsApp click will use it */
  setActiveTrigger(trig: TriggerDef) {
    this.activeMessage = trig.autoMessage;
    this.activeTriggerId = trig.id;
    this.activeUntil = Date.now() + this.STALE_MS;
    this.installInterceptor();
  }

  clearActiveTrigger() {
    this.activeMessage = null;
    this.activeTriggerId = null;
    this.activeUntil = 0;
  }

  /** Default friendly fallback message when no trigger is active */
  readonly DEFAULT_MESSAGE = 'Hi! How can you help me?';

  /** Build a WhatsApp deep link — appends current page URL so agent has context */
  buildWhatsAppUrl(message?: string): string {
    const baseText = message || this.getCurrentMessage() || this.DEFAULT_MESSAGE;

    let pageUrl = (typeof window !== 'undefined' && window.location?.href) || '';

    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);

      // Read tracking_id from URL or reuse the session-stable ID
      const trackingId = urlParams.get('tracking_id') || this.sessionTrackingId;

      // Read campaign_name from URL (handle typo variant too)
      const campaignName = urlParams.get('campaign_name') || urlParams.get('campaing_name') || null;

      // Append tracking params to the page URL so agent sees them in message
      const baseUrl = window.location.origin + window.location.pathname;
      const msgParams = new URLSearchParams();
      msgParams.set('tracking_id', trackingId);
      if (campaignName) msgParams.set('campaign_name', campaignName);
      pageUrl = baseUrl + '?' + msgParams.toString();
    }

    const text = pageUrl ? `${baseText}\n\nPage URL — ${pageUrl}` : baseText;
    return `https://wa.me/${this.phone}?text=${encodeURIComponent(text)}`;
  }

  getCurrentMessage(): string | null {
    if (!this.activeMessage) return null;
    if (Date.now() > this.activeUntil) {
      this.clearActiveTrigger();
      return null;
    }
    return this.activeMessage;
  }

  getCurrentTriggerId(): string | null {
    return this.getCurrentMessage() ? this.activeTriggerId : null;
  }

  /** Open WhatsApp directly with a specific trigger's message */
  openWhatsApp(trig: TriggerDef) {
    this.setActiveTrigger(trig);
    const url = this.buildWhatsAppUrl(trig.autoMessage);
    this.clicked$.next();
    if (!this.disableOpen) window.open(url, '_blank', 'noopener');
  }

  /**
   * Install global interceptors that rewrite ANY WhatsApp link/open call
   * (Interakt button, footer button, etc.) to use the active trigger message.
   */
  private installInterceptor() {
    if (this.interceptorInstalled) return;
    this.interceptorInstalled = true;

    const isWhatsAppUrl = (s: string): boolean =>
      typeof s === 'string' && (s.includes('api.whatsapp.com/send') || s.includes('wa.me/'));

    const buildPageUrlWithTracking = (): string => {
      const urlParams = new URLSearchParams(window.location.search);
      const trackingId   = urlParams.get('tracking_id')   || this.sessionTrackingId;
      const campaignName = urlParams.get('campaign_name') || urlParams.get('campaing_name') || null;
      const msgParams = new URLSearchParams();
      msgParams.set('tracking_id', trackingId);
      if (campaignName) msgParams.set('campaign_name', campaignName);
      return window.location.origin + window.location.pathname + '?' + msgParams.toString();
    };

    const swapWhatsAppUrl = (url: string): string => {
      const msg = this.getCurrentMessage();
      if (!msg) return url;
      try {
        const u = new URL(url, window.location.origin);
        if (u.hostname.includes('whatsapp.com') || u.hostname.includes('wa.me')) {
          const pageUrl = buildPageUrlWithTracking();
          const fullText = `${msg}\n\nPage URL — ${pageUrl}`;
          u.searchParams.set('text', fullText);
          return u.toString();
        }
      } catch {}
      return url;
    };

    this.zone.runOutsideAngular(() => {
      // 1) Click interceptor (capture phase — beats Interakt's handler)
      document.addEventListener(
        'click',
        (e: Event) => {
          if (!this.getCurrentMessage()) return;
          let el = e.target as HTMLElement | null;
          while (el && el !== document.body) {
            const href = (el as HTMLAnchorElement).href || el.getAttribute?.('href') || '';
            if (isWhatsAppUrl(href)) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.zone.run(() => this.clicked$.next());
              if (!this.disableOpen) window.open(swapWhatsAppUrl(href), '_blank', 'noopener');
              return;
            }
            el = el.parentElement;
          }
        },
        true,
      );

      // 2) Patch window.open
      const origOpen = window.open;
      (window as any).open = function (url?: string | URL, target?: string, features?: string) {
        const finalUrl = typeof url === 'string' && isWhatsAppUrl(url) ? swapWhatsAppUrl(url) : url;
        return origOpen.call(window, finalUrl as any, target, features);
      };

      // 3) Patch location.assign / replace
      const loc = window.location;
      const origAssign = loc.assign.bind(loc);
      const origReplace = loc.replace.bind(loc);
      loc.assign = (url: string | URL) => {
        const u = typeof url === 'string' && isWhatsAppUrl(url) ? swapWhatsAppUrl(url) : url;
        return origAssign(u as any);
      };
      loc.replace = (url: string | URL) => {
        const u = typeof url === 'string' && isWhatsAppUrl(url) ? swapWhatsAppUrl(url) : url;
        return origReplace(u as any);
      };

      // 4) MutationObserver — patch new <a href> elements Interakt injects
      const patchAnchors = (root: ParentNode | Document) => {
        if (!this.getCurrentMessage()) return;
        const anchors = root.querySelectorAll?.('a[href*="api.whatsapp.com"], a[href*="wa.me"]');
        anchors?.forEach((a) => {
          const href = (a as HTMLAnchorElement).href;
          if (isWhatsAppUrl(href)) {
            (a as HTMLAnchorElement).href = swapWhatsAppUrl(href);
          }
        });
      };
      patchAnchors(document);
      const obs = new MutationObserver((muts) => {
        for (const m of muts) {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === 1) patchAnchors(n as unknown as ParentNode);
          });
          if (m.type === 'attributes' && m.target.nodeType === 1) {
            const parent = (m.target as Element).parentNode as unknown as ParentNode | null;
            if (parent) patchAnchors(parent);
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    });
  }
}
